import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Popconfirm, Spin, Tooltip } from 'antd'
import { formatDistanceToNow } from 'date-fns'
import { ChatSession } from 'features/drilldown/panels/global/llmPanel/gongChatApi'
import { SessionSidebarProps } from 'features/drilldown/panels/global/llmPanel/types'
import { ChevronLeft, ChevronRight, MessageSquare } from 'lucide-react'
import React, { FC, useCallback } from 'react'
import styled from 'styled-components'

const SidebarContainer = styled.aside<{ $collapsed: boolean }>`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: ${({ $collapsed }) => ($collapsed ? '2.5rem' : '13rem')};
  border-right: 1px solid ${({ theme }) => theme.forecast.border};
  background: ${({ theme }) => theme.forecast.cardBackground};
  min-height: 0;
  transition: width 0.2s ease;
`

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
`

const SessionList = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding: 0.375rem;
`

const DeleteButton = styled(Button)`
  opacity: 0;
  flex-shrink: 0;
`

const SessionRow = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
  width: 100%;
  padding: 0.5rem;
  border: none;
  border-radius: 0.375rem;
  background: ${({ $active, theme }) =>
    $active ? theme.forecast.primary + '18' : 'transparent'};
  color: ${({ theme }) => theme.forecast.defaultText};
  cursor: pointer;
  text-align: left;

  &:hover {
    background: ${({ $active, theme }) =>
      $active ? theme.forecast.primary + '24' : theme.forecast.border + '80'};
  }

  &:hover ${DeleteButton} {
    opacity: 1;
  }
`

const SessionLabel = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 0.8125rem;
  line-height: 1.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const SessionMeta = styled.div`
  font-size: 0.6875rem;
  line-height: 1rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const EmptyState = styled.div`
  padding: 0.75rem 0.5rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  text-align: center;
`

const CollapseToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.375rem;
  border: none;
  border-top: 1px solid ${({ theme }) => theme.forecast.border};
  background: transparent;
  color: ${({ theme }) => theme.forecast.secondaryText};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.forecast.defaultText};
    background: ${({ theme }) => theme.forecast.border + '40'};
  }
`

function formatSessionLabel(session: ChatSession): string {
  const title = session.title?.trim()
  if (title) return title

  const preview = session.preview?.trim()
  if (preview) return preview

  if (session.created_at > 0) {
    return new Date(session.created_at).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  return 'Untitled chat'
}

function formatSessionTime(updatedAt: number): string {
  if (updatedAt <= 0) return ''
  return formatDistanceToNow(new Date(updatedAt), { addSuffix: true })
}

export const SessionSidebar: FC<SessionSidebarProps> = ({
  sessions,
  activeSessionId,
  isLoading,
  collapsed,
  onSelect,
  onNew,
  onDelete,
  onToggleCollapse,
}) => {
  const handleDelete = useCallback(
    (sessionId: string) => {
      void onDelete(sessionId)
    },
    [onDelete]
  )

  if (collapsed) {
    return (
      <SidebarContainer $collapsed>
        <SidebarHeader>
          <Tooltip title='New chat' placement='right'>
            <Button
              type='text'
              size='small'
              icon={<PlusOutlined />}
              onClick={onNew}
              aria-label='New chat'
            />
          </Tooltip>
        </SidebarHeader>
        <CollapseToggle onClick={onToggleCollapse} aria-label='Expand sidebar'>
          <ChevronRight size={16} />
        </CollapseToggle>
      </SidebarContainer>
    )
  }

  return (
    <SidebarContainer $collapsed={false}>
      <SidebarHeader>
        <Button
          type='primary'
          size='small'
          icon={<PlusOutlined />}
          onClick={onNew}
          block>
          New chat
        </Button>
      </SidebarHeader>

      <SessionList>
        {isLoading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '1rem',
            }}>
            <Spin size='small' />
          </div>
        ) : sessions.length === 0 ? (
          <EmptyState>No past conversations</EmptyState>
        ) : (
          sessions.map((session) => {
            const isActive = session.id === activeSessionId
            return (
              <SessionRow
                key={session.id}
                type='button'
                $active={isActive}
                onClick={() => onSelect(session.id)}>
                <MessageSquare
                  size={14}
                  style={{ flexShrink: 0, marginTop: 2 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <SessionLabel>{formatSessionLabel(session)}</SessionLabel>
                  <SessionMeta>
                    {formatSessionTime(session.updated_at)}
                  </SessionMeta>
                </div>
                <Popconfirm
                  title='Delete this conversation?'
                  description='This permanently deletes the conversation and its messages.'
                  okText='Delete'
                  cancelText='Cancel'
                  onConfirm={() => handleDelete(session.id)}
                  onPopupClick={(event) => event.stopPropagation()}>
                  <DeleteButton
                    type='text'
                    size='small'
                    danger
                    icon={<DeleteOutlined />}
                    aria-label='Delete conversation'
                    onClick={(event) => event.stopPropagation()}
                  />
                </Popconfirm>
              </SessionRow>
            )
          })
        )}
      </SessionList>

      <CollapseToggle onClick={onToggleCollapse} aria-label='Collapse sidebar'>
        <ChevronLeft size={16} />
      </CollapseToggle>
    </SidebarContainer>
  )
}
